import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { ArrowLeft, Bell, MessageCircle, Mail, Clock, User, LogOut, Send, Eye, Loader2 } from "lucide-react";

export default function MayaManthanSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    daily_reminder_time: "06:00",
    whatsapp_updates: false,
    email_updates: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    try {
      await axios.put(`${API}/maya-manthan/settings`, null, {
        params: { [key]: value }
      });
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
      // Revert
      fetchSettings();
    }
  };

  const previewDigest = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API}/maya-manthan/digest/preview`);
      setDigestPreview(response.data);
    } catch (error) {
      toast.error("Failed to load digest preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendTestDigest = async () => {
    setSendingDigest(true);
    try {
      const response = await axios.post(`${API}/maya-manthan/digest/send-test`);
      if (response.data.status === 'success') {
        toast.success("Weekly digest sent to your email!");
      } else if (response.data.status === 'skipped') {
        toast.info(response.data.reason || "Email service not configured");
      } else {
        toast.error(response.data.error || "Failed to send digest");
      }
    } catch (error) {
      toast.error("Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="maya-settings">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-serif text-4xl text-white mb-4">Settings</h1>
          <p className="text-gray-400">Manage your preferences and notifications</p>
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-4">Profile</h3>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-indigo-600 flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <p className="text-white font-medium text-lg">{user?.name}</p>
              <p className="text-gray-500">{user?.email}</p>
            </div>
          </div>
        </motion.div>

        {/* Notifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-4">Notifications</h3>
          
          <div className="space-y-4">
            {/* Push Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-white">Push Notifications</p>
                  <p className="text-gray-500 text-sm">Daily practice reminders</p>
                </div>
              </div>
              <Switch
                checked={settings.notifications_enabled}
                onCheckedChange={(checked) => updateSetting('notifications_enabled', checked)}
              />
            </div>

            {/* Daily Reminder Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-white">Reminder Time</p>
                  <p className="text-gray-500 text-sm">When to send daily reminder</p>
                </div>
              </div>
              <Input
                type="time"
                value={settings.daily_reminder_time}
                onChange={(e) => updateSetting('daily_reminder_time', e.target.value)}
                className="w-32 bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Email Updates */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-white">Email Updates</p>
                  <p className="text-gray-500 text-sm">Weekly insights & tips</p>
                </div>
              </div>
              <Switch
                checked={settings.email_updates}
                onCheckedChange={(checked) => updateSetting('email_updates', checked)}
              />
            </div>

            {/* WhatsApp Updates */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-white">WhatsApp Updates</p>
                  <p className="text-gray-500 text-sm">Receive updates via WhatsApp</p>
                </div>
              </div>
              <Switch
                checked={settings.whatsapp_updates}
                onCheckedChange={(checked) => updateSetting('whatsapp_updates', checked)}
              />
            </div>
          </div>
        </motion.div>

        {/* Weekly Digest Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-4">Weekly Email Digest</h3>
          <p className="text-gray-500 text-sm mb-4">
            Get a beautiful summary of your practice, mood trends, and personalized recommendations every week.
          </p>
          
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              onClick={previewDigest}
              disabled={loadingPreview}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {loadingPreview ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Preview Digest
            </Button>
            <Button
              onClick={sendTestDigest}
              disabled={sendingDigest || !settings.email_updates}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {sendingDigest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test Digest
            </Button>
          </div>

          {/* Digest Preview */}
          {digestPreview && (
            <div className="bg-white/5 rounded-xl p-4 space-y-4">
              <h4 className="text-white font-medium">Your Weekly Summary Preview</h4>
              
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-indigo-500/10 rounded-lg p-3">
                  <p className="text-2xl font-bold text-indigo-400">{digestPreview.practice_stats?.total_sessions || 0}</p>
                  <p className="text-xs text-gray-500">Sessions</p>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-400">{digestPreview.practice_stats?.total_minutes || 0}</p>
                  <p className="text-xs text-gray-500">Minutes</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-400">{digestPreview.streak?.current || 0}</p>
                  <p className="text-xs text-gray-500">Day Streak</p>
                </div>
              </div>

              {Object.keys(digestPreview.mood_trends || {}).length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Mood Trends</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(digestPreview.mood_trends || {}).map(([mood, count]) => (
                      <span key={mood} className="px-2 py-1 bg-white/5 rounded-full text-xs text-gray-300 capitalize">
                        {mood}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-gray-400 text-sm mb-2">Insight</p>
                <p className="text-white text-sm">{digestPreview.insights}</p>
              </div>

              {digestPreview.recommendations?.length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Recommendations</p>
                  <ul className="space-y-1">
                    {digestPreview.recommendations.slice(0, 2).map((rec, i) => (
                      <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                        <span className="text-amber-500">•</span> {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* App Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-4">Quick Links</h3>
          
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/maya-manthan/guide')}
              className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 text-gray-400 transition-colors"
            >
              About Your Guide
            </button>
            <button 
              onClick={() => navigate('/maya-manthan/courses')}
              className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 text-gray-400 transition-colors"
            >
              View Courses
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 text-gray-400 transition-colors"
            >
              ChakraFlow Dashboard
            </button>
          </div>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={logout}
            variant="ghost"
            className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5 mr-2" /> Sign Out
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
